import { Component, EventEmitter, Output, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import ordnanceOptions from '../../../../assets/data/ordnances.json';
import { groupsForReportType, ReportType } from '../../../utilities/ordnanceCategories';

// Use string paths for Angular asset references
const IMAGES: { [key: string]: string } = {
  BOMBS_1: 'assets/images/ordnances/bombs/bomb-1.png',
  BOMBS_2: 'assets/images/ordnances/bombs/bomb-2.png',
  BOMBS_3: 'assets/images/ordnances/bombs/bomb-3.png',
  SUBMUNITIONS_1: 'assets/images/ordnances/submunitions/submunition-1.png',
  SUBMUNITIONS_2: 'assets/images/ordnances/submunitions/submunition-2.png',
  SUBMUNITIONS_3: 'assets/images/ordnances/submunitions/submunition-3.png',
  SUBMUNITIONS_4: 'assets/images/ordnances/submunitions/submunition-4.png',
  MORTAR_PROJECTILES_1:
    'assets/images/ordnances/mortar-projectiles/mortar-projectile-1.png',
  MORTAR_PROJECTILES_2:
    'assets/images/ordnances/mortar-projectiles/mortar-projectile-2.png',
  MORTAR_PROJECTILES_3:
    'assets/images/ordnances/mortar-projectiles/mortar-projectile-3.png',
  ROCKETS_1: 'assets/images/ordnances/rockets/rocket-1.png',
  ROCKETS_2: 'assets/images/ordnances/rockets/rocket-2.png',
  ROCKETS_3: 'assets/images/ordnances/rockets/rocket-3.png',
  GUIDED_MISSILES_1:
    'assets/images/ordnances/guided-missiles/guided-missile-1.png',
  GUIDED_MISSILES_2:
    'assets/images/ordnances/guided-missiles/guided-missile-2.png',
  GRENADES_1: 'assets/images/ordnances/grenades/grenade-1.png',
  GRENADES_2: 'assets/images/ordnances/grenades/grenade-2.png',
  GRENADES_3: 'assets/images/ordnances/grenades/grenade-3.png',
  GRENADES_4: 'assets/images/ordnances/grenades/grenade-4.png',
  GRENADES_5: 'assets/images/ordnances/grenades/grenade-5.png',
  LANDMINES_1: 'assets/images/ordnances/landmines/landmine-1.png',
  LANDMINES_2: 'assets/images/ordnances/landmines/landmine-2.png',
  LANDMINES_3: 'assets/images/ordnances/landmines/landmine-3.png',
  LANDMINES_4: 'assets/images/ordnances/landmines/landmine-4.png',
  LANDMINES_5: 'assets/images/ordnances/landmines/landmine-5.png',
  LANDMINES_6: 'assets/images/ordnances/landmines/landmine-6.png',
  LANDDRONE_1: 'assets/images/ordnances/land-drones/landdrone-1.png',
  LANDDRONE_2: 'assets/images/ordnances/land-drones/landdrone-2.png',
  LANDDRONE_3: 'assets/images/ordnances/land-drones/landdrone-3.png',
  AIRDRONE_1: 'assets/images/ordnances/air-drones/airdrone-1.png',
  AIRDRONE_2: 'assets/images/ordnances/air-drones/airdrone-2.png',
  AIRDRONE_3: 'assets/images/ordnances/air-drones/airdrone-3.png',
  SEADRONE_1: 'assets/images/ordnances/sea-drones/seadrone-1.png',
  SEADRONE_2: 'assets/images/ordnances/sea-drones/seadrone-2.png',
};

export function getItemsForOrdnanceType(type: string, sizes: number) {
  const keyPrefix = type.replace('-', '_').toUpperCase();
  return Array.from({ length: sizes }, (_, index) => {
    const key = `${keyPrefix}_${index + 1}`;
    return {
      key,
      name: key,
      image: IMAGES[key],
    };
  });
}

@Component({
  selector: 'app-ordnance-search-modal',
  standalone: true,
  templateUrl: './ordnance-search-modal.component.html',
  styleUrls: ['./ordnance-search-modal.component.scss'],
  imports: [CommonModule, DialogModule, ButtonModule],
})
export class OrdnanceSearchModalComponent {
  @Output() close = new EventEmitter<void>();
  @Output() select = new EventEmitter<any>();
  @Input() visible = false;

  // UXO reports (the default) don't offer the SRE items; pass 'SRE' to show only those.
  @Input() set reportType(value: ReportType | null | undefined) {
    this.visibleGroups = groupsForReportType(this.ordnanceGroups, value);
  }

  ordnanceGroups = [
    { name: $localize`:@@ordnance.bombs:Bombs`, type: 'bombs', items: getItemsForOrdnanceType('bombs', 3) },
    { name: $localize`:@@ordnance.submunitions:Submunitions`, type: 'submunitions', items: getItemsForOrdnanceType('submunitions', 4) },
    { name: $localize`:@@ordnance.mortarProjectiles:Mortar/Projectiles`, type: 'mortar-projectiles', items: getItemsForOrdnanceType('mortar-projectiles', 3) },
    { name: $localize`:@@ordnance.rockets:Rockets`, type: 'rockets', items: getItemsForOrdnanceType('rockets', 3) },
    { name: $localize`:@@ordnance.guidedMissiles:Guided Missiles`, type: 'guided-missiles', items: getItemsForOrdnanceType('guided-missiles', 2) },
    { name: $localize`:@@ordnance.grenades:Grenades`, type: 'grenades', items: getItemsForOrdnanceType('grenades', 5) },
    { name: $localize`:@@ordnance.landmines:Landmines`, type: 'landmines', items: getItemsForOrdnanceType('landmines', 6) },
    { name: $localize`:@@ordnance.landDrones:Land Drones (UGV)`, type: 'landdrone', items: getItemsForOrdnanceType('landdrone', 3) },
    { name: $localize`:@@ordnance.airDrones:Air Drones (UAS)`, type: 'airdrone', items: getItemsForOrdnanceType('airdrone', 3) },
    { name: $localize`:@@ordnance.seaDrones:Sea Drones (UMS)`, type: 'seadrone', items: getItemsForOrdnanceType('seadrone', 2) },
    {
      name: $localize`:@@ordnance.other:Other`,
      type: 'other',
      items: [
        { name: $localize`:@@classification.osseousMaterial:Possible Osseous Material`, image: undefined, key: 'OTHER_POSSIBLE_OSSEOUS_MATERIAL' },
        { name: $localize`:@@classification.materialEvidence:Possible Material Evidence`, image: undefined, key: 'OTHER_POSSIBLE_MATERIAL_EVIDENCE' },
        { name: $localize`:@@classification.lifeSupportEquipment:Possible Life Support Equipment`, image: undefined, key: 'OTHER_POSSIBLE_LIFE_SUPPORT_EQUIPMENT' },
        { name: $localize`:@@classification.aircraftWreckage:Possible Aircraft Wreckage`, image: undefined, key: 'OTHER_POSSIBLE_AIRCRAFT_WRECKAGE' },
      ]
    },
    {
      name: $localize`:@@classification.unknown:Unknown`,
      items: [
        { name: $localize`:@@classification.unknown:Unknown`, image: 'assets/images/ordnances/unknown/unknown.png', key: 'UNKNOWN' },
      ],
    },
  ];

  // What the dialog lists. Computed once, and again when reportType changes, so the list keeps the same
  // objects between change detection runs instead of being rebuilt (and its buttons re-created) every time.
  visibleGroups = groupsForReportType(this.ordnanceGroups, 'UXO');

  isSelectingSize = false;
  selectedOrdnance: { name: string; image: string; type: string } | null = null;
  selectedSizeKey: string | null = null;
  sizeOptions: any[] = [];
  // Items that have no size to choose. These are compared with the item's key (not its name).
  noSizeOrdnanceNames: String[] = [
    'UNKNOWN',
    'OTHER_POSSIBLE_OSSEOUS_MATERIAL',
    'OTHER_POSSIBLE_MATERIAL_EVIDENCE',
    'OTHER_POSSIBLE_LIFE_SUPPORT_EQUIPMENT',
    'OTHER_POSSIBLE_AIRCRAFT_WRECKAGE'
  ]

  onSelect(ordName: string) {
    let found: any = null;
    let ordType = '';
    for (const group of this.ordnanceGroups) {
      const match = group.items.find((item: any) => item.name === ordName);
      if (match) {
        found = match;
        ordType = group.type;
        break;
      }
    }

    if (!found) return;
    this.selectedOrdnance = { name: found.name, image: found.image, type: ordType };

    // Use key for comparison, not name
    if (this.noSizeOrdnanceNames.includes(found.key)) {
      this.selectedSizeKey = found.key;
      this.isSelectingSize = false;
      this.onSave();
      return;
    }

    this.sizeOptions = (ordnanceOptions as any[]).filter(opt => opt.type === ordType);
    this.isSelectingSize = true;
  }

  onSizeSelect(key: string) {
    this.selectedSizeKey = key;
  }

  goBack() {
    this.isSelectingSize = false;
    this.selectedSizeKey = null;
  }

  onSave() {
    if (this.selectedSizeKey) {
      // Find the full data object from ordnanceOptions
      const selectedData = (ordnanceOptions as any[]).find(
        (opt) => opt.key === this.selectedSizeKey
      );
      this.select.emit({ key: this.selectedSizeKey, data: selectedData });
    }
    this.onCancel();
  }

  onCancel() {
    if (this.isSelectingSize) {
      this.isSelectingSize = false;
      this.selectedOrdnance = null;
      this.selectedSizeKey = null;
      this.sizeOptions = [];
    } else {
      this.close.emit();
    }
  }
}
